// Building → Budgets (/app/building?sub=budgets): every development's Budget & charges in one table — this year's
// service-charge budget, spend against it from the ledger, the average per unit, the reserve fund and what is under
// contract. Read from GET /building/overview (the same figures as each development's Budget & charges); "Set" / "Edit"
// opens the same budget editor. Money is never added across currencies.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCan } from '../../hooks/usePermission';
import { StatTile } from '../charts/StatTile';
import { Badge, Button, PageHeader } from '../ui';
import { BudgetEditor } from '../accounts/BudgetEditor';
import { demandLabel } from '../../lib/fees';
import { formatDate, formatMoney } from '../../lib/viz';
import { buildingOverview, type DevelopmentBudget, type MoneyByCurrency, moneyTile, tileRow } from '../../lib/portfolio';
import { Quiet, SectionHead } from './bits';

const total = (rows: DevelopmentBudget[], pick: (b: DevelopmentBudget) => number | null): MoneyByCurrency[] =>
  [...rows.reduce((m, b) => { const v = pick(b); return v === null ? m : m.set(b.currency, (m.get(b.currency) ?? 0) + v); }, new Map<string, number>())]
    .map(([currency, minor]) => ({ currency, minor })).filter((x) => x.minor !== 0).sort((a, b) => b.minor - a.minor);

export function BuildingBudgetsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/building/overview'], queryFn: buildingOverview });
  const canEdit = useCan('budgets.update');
  const [editing, setEditing] = useState<DevelopmentBudget | null>(null);
  const rows = q.data?.budgets ?? [];
  const m = (v: number | null, c: string) => (v === null ? <span className="text-ink-300">—</span> : formatMoney(v, c));
  const set = rows.filter((b) => b.budgetMinor !== null).length;
  const budget = moneyTile(total(rows, (b) => b.budgetMinor));
  const spent = moneyTile(total(rows, (b) => b.actualMinor));
  const committed = moneyTile(total(rows, (b) => b.committedContractsMinor));
  const year = rows[0]?.year ?? new Date().getUTCFullYear();

  return (
    <div className="space-y-2.5">
      <PageHeader title="Budgets & service charges" />
      <div className="grid gap-2.5" style={tileRow}>
        <StatTile compact label={`Budgets set ${year}`} value={q.isLoading ? '—' : `${set} of ${rows.length}`} status={rows.length && set < rows.length ? 'warning' : undefined} hint={set < rows.length ? `${rows.length - set} not set yet` : 'every development'} />
        <StatTile compact label="Budgeted" value={budget.value} hint={budget.hint ?? 'this year, all developments'} />
        <StatTile compact label="Spent so far" value={spent.value} hint={spent.hint ?? 'from the ledger, this year'} />
        <StatTile compact label="Under contract pa" value={committed.value} hint={committed.hint ?? 'service-charge contracts'} />
      </div>

      <section className="surface overflow-hidden">
        <SectionHead title={`Service-charge budgets ${year}`} note={`${rows.length} development${rows.length === 1 ? '' : 's'}`} />
        {q.isLoading ? <div className="h-40 animate-pulse" /> : !rows.length ? <Quiet>No development in your scope.</Quiet> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-[12.5px]">
              <thead>
                <tr className="bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500">
                  <th className="px-3 py-1.5 text-left">Development</th>
                  <th className="px-3 py-1.5 text-right">Budget</th><th className="px-3 py-1.5 text-right">Spent</th>
                  <th className="px-3 py-1.5 text-right">Per unit</th><th className="px-3 py-1.5 text-right">Reserve fund</th>
                  <th className="px-3 py-1.5 text-right">Under contract</th><th className="px-3 py-1.5 text-left">Year end</th>
                  <th className="px-3 py-1.5 text-left">Demands</th><th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {rows.map((b) => (
                  <tr key={b.propertyId} className="hover:bg-ink-50/60">
                    <td className="px-3 py-1.5">
                      <Link to={`/app/properties/${b.propertyId}?tab=building&sub=money`} className="font-medium text-ink-900 hover:text-bronze-700 hover:underline">{b.name}</Link>
                      <div className="text-[11px] text-ink-400">{b.unitCount || b.units || 0} units</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {b.budgetMinor === null ? <span className="text-amber-700">Not set</span> : <>{formatMoney(b.budgetMinor, b.currency)} {b.status === 'DRAFT' && <Badge value="Draft" tone="warning" />}</>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{m(b.actualMinor, b.currency)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{m(b.perUnitMinor, b.currency)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{m(b.reserveBalanceMinor, b.currency)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{b.committedContractsMinor ? formatMoney(b.committedContractsMinor, b.currency) : <span className="text-ink-300">—</span>}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">{b.yearEnd ? formatDate(b.yearEnd) : <span className="text-ink-300">—</span>}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">{demandLabel(b.demandFrequency) ?? <span className="text-ink-300">—</span>}{b.nextDemand && <div className="text-[11px] text-ink-400">next {formatDate(b.nextDemand)}</div>}</td>
                    <td className="px-3 py-1.5 text-right">{canEdit && <Button size="sm" variant="secondary" onClick={() => setEditing(b)}>{b.budgetMinor === null ? 'Set' : 'Edit'}</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="rounded-xl border border-ink-100 bg-ink-50 px-4 py-3 text-[12.5px] leading-relaxed text-ink-600">
        Service-charge schedules and apportionment per schedule are not configured yet — needs the client's apportionment method.
        Until then each figure is for the whole development, and the average per unit is the budget divided equally.
      </p>
      {editing && <BudgetEditor propertyId={editing.propertyId} year={editing.year} open onClose={() => { setEditing(null); void qc.invalidateQueries({ queryKey: ['/building/overview'] }); }} />}
    </div>
  );
}
